export type TrechoDoDiff = { tipo: 'igual' | 'removido' | 'adicionado'; texto: string };

/**
 * Tokens = palavras E os espaços entre elas, para o texto remontado sair com
 * a mesma quebra de linha/espaçamento do original. Pontuação fica grudada na
 * palavra ("porém,"), o que é o desejado: a revisão de vírgula aparece como
 * troca da palavra inteira, que é como o médico lê.
 */
function tokenizar(texto: string): string[] {
  return texto.split(/(\s+)/).filter((token) => token.length > 0);
}

/**
 * Diff por palavra (LCS) entre o rascunho e a sugestão da IA — só para
 * destacar o que mudou no painel de sugestão.
 *
 * Quadrático em tokens, e está tudo bem: o DTO limita o texto a 4000
 * caracteres (~700 palavras → ~1400 tokens com os espaços), e a tabela é
 * montada uma vez por sugestão.
 */
export function diffDePalavras(original: string, revisado: string): TrechoDoDiff[] {
  const a = tokenizar(original);
  const b = tokenizar(revisado);

  // lcs[i][j] = tamanho da maior subsequência comum entre a[i..] e b[j..].
  const lcs: number[][] = Array.from({ length: a.length + 1 }, () => new Array<number>(b.length + 1).fill(0));
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      lcs[i]![j] = a[i] === b[j] ? lcs[i + 1]![j + 1]! + 1 : Math.max(lcs[i + 1]![j]!, lcs[i]![j + 1]!);
    }
  }

  const trechos: TrechoDoDiff[] = [];
  function empurrar(tipo: TrechoDoDiff['tipo'], texto: string) {
    const ultimo = trechos.at(-1);
    if (ultimo?.tipo === tipo) ultimo.texto += texto;
    else trechos.push({ tipo, texto });
  }

  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      empurrar('igual', a[i]!);
      i++;
      j++;
    } else if (lcs[i + 1]![j]! >= lcs[i]![j + 1]!) {
      empurrar('removido', a[i]!);
      i++;
    } else {
      empurrar('adicionado', b[j]!);
      j++;
    }
  }
  while (i < a.length) empurrar('removido', a[i++]!);
  while (j < b.length) empurrar('adicionado', b[j++]!);

  return trechos;
}
